import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import PlayerField from '../components/PlayerField';
import { getApiBaseUrl, getApiOrigin } from '../apiBase';
import Logo from '../components/Logo';

const API = getApiBaseUrl();

export default function VideoPage() {
    const { t } = useTranslation(['uploadPage', 'common']);
    const { id } = useParams();
    const [video, setVideo] = useState(null);
    const [comment, setComment] = useState('');
    const [liked, setLiked] = useState(false);
    const videoRef = useRef(null);

    useEffect(() => {
        axios.get(`${API}/video/${id}`).then(r => setVideo(r.data));
    }, [id]);

    function jumpToTime(seconds) {
        if (videoRef.current) {
            videoRef.current.currentTime = seconds;
            videoRef.current.play();
        }
    }

    function formatTime(seconds) {
        const m = Math.floor(seconds / 60);
        const s = Math.floor(seconds % 60);
        return `${m}:${s.toString().padStart(2, '0')}`;
    }

    async function handleLike() {
        const token = localStorage.getItem('token');
        if (!token) return alert(t('uploadPage:loginToLike'));
        if (liked) return;
        const res = await axios.post(`${API}/video/${id}/like`, {}, {
            headers: { Authorization: `Bearer ${token}` }
        });
        setVideo(v => ({ ...v, likes: res.data.likes }));
        setLiked(true);
    }

    async function handleComment(e) {
        e.preventDefault();
        const token = localStorage.getItem('token');
        if (!token) return alert(t('uploadPage:loginToComment'));
        const res = await axios.post(`${API}/video/${id}/comments`, { text: comment }, {
            headers: { Authorization: `Bearer ${token}` }
        });
        setVideo(v => ({ ...v, comments: [...v.comments, res.data] }));
        setComment('');
    }

    if (!video) return (
        <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            height: '100vh', fontFamily: 'Segoe UI, Arial, sans-serif'
        }}>
            <div style={{ textAlign: 'center', color: '#888' }}>
                <div style={{ fontSize: '40px', marginBottom: '12px' }}>🎬</div>
                <div>{t('uploadPage:videoLoading')}</div>
            </div>
        </div>
    );

    return (
        <div style={{ fontFamily: "'Segoe UI', Arial, sans-serif", background: 'var(--sp-gray-50)', minHeight: '100vh' }}>

            <header style={{
                background: '#1A1A1A', color: 'white',
                padding: '0 24px', height: '56px',
                display: 'flex', alignItems: 'center', gap: '16px'
            }}>
                <a href="/" style={{
                    color: '#94a3b8', fontSize: '13px', textDecoration: 'none',
                    display: 'flex', alignItems: 'center', gap: '6px'
                }}>
                    {t('uploadPage:back')}
                </a>
                <span style={{ color: '#334155', fontSize: '18px' }}>|</span>
                <Logo variant="dark" height={32} />
            </header>

            <div style={{ maxWidth: '960px', margin: '0 auto', padding: '24px' }}>

                <div style={{
                    background: '#000', borderRadius: '16px',
                    overflow: 'hidden', marginBottom: '20px',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
                }}>
                    <video
                        ref={videoRef}
                        src={`${getApiOrigin()}${video.filePath}`}
                        controls
                        style={{ width: '100%', display: 'block', maxHeight: '520px' }}
                    />
                </div>

                <div style={{
                    background: 'white', borderRadius: '14px',
                    padding: '20px 24px', marginBottom: '16px',
                    border: '1px solid var(--sp-gray-100)'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
                        <h1 style={{ fontSize: '22px', fontWeight: '700', margin: 0 }}>{video.title}</h1>
                        <button
                            onClick={handleLike}
                            style={{
                                padding: '10px 20px', borderRadius: '10px', border: 'none',
                                background: liked ? '#fce4ec' : 'var(--sp-gray-50)',
                                color: liked ? '#c62828' : '#666',
                                cursor: liked ? 'default' : 'pointer',
                                fontSize: '15px', fontWeight: '600', flexShrink: 0,
                                transition: 'all 0.2s'
                            }}
                        >
                            {liked ? '❤️' : '🤍'} {video.likes}
                        </button>
                    </div>

                    <div style={{ display: 'flex', gap: '16px', marginTop: '10px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '13px', color: '#888' }}>👤 {video.author}</span>
                        <span style={{ fontSize: '13px', color: '#888' }}>👁 {t('uploadPage:views', { count: video.views })}</span>
                        <span style={{ fontSize: '13px', color: '#888' }}>
                            📅 {new Date(video.createdAt).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })}
                        </span>
                    </div>

                    {video.description && (
                        <p style={{ margin: '14px 0 0', fontSize: '15px', color: '#444', lineHeight: '1.6' }}>
                            {video.description}
                        </p>
                    )}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>

                    {video.timestamps?.length > 0 && (
                        <div style={{
                            background: 'white', borderRadius: '14px',
                            padding: '20px', border: '1px solid var(--sp-gray-100)'
                        }}>
                            <h3 style={{ margin: '0 0 14px', fontSize: '15px', fontWeight: '700' }}>
                                {t('uploadPage:timestamps')}
                            </h3>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                {video.timestamps.map(ts => (
                                    <div
                                        key={ts.id}
                                        onClick={() => jumpToTime(ts.timeSeconds)}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: '10px',
                                            padding: '8px 10px', borderRadius: '8px',
                                            cursor: 'pointer', transition: 'background 0.15s',
                                            border: '1px solid #f0f0f0'
                                        }}
                                        onMouseEnter={e => e.currentTarget.style.background = 'var(--sp-gray-50)'}
                                        onMouseLeave={e => e.currentTarget.style.background = 'white'}
                                    >
                                        <span style={{
                                            background: '#1A1A1A', color: 'white',
                                            padding: '3px 8px', borderRadius: '6px',
                                            fontSize: '12px', fontFamily: 'monospace', flexShrink: 0
                                        }}>
                                            {formatTime(ts.timeSeconds)}
                                        </span>
                                        <span style={{ fontSize: '13px', color: '#444' }}>{ts.label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {video.playerPositions?.length > 0 && (
                        <div style={{
                            background: 'white', borderRadius: '14px',
                            padding: '20px', border: '1px solid var(--sp-gray-100)'
                        }}>
                            <h3 style={{ margin: '0 0 14px', fontSize: '15px', fontWeight: '700' }}>
                                {t('uploadPage:formation')}
                            </h3>
                            <PlayerField
                                fieldType={video.sportFieldType}
                                positions={video.playerPositions}
                            />
                        </div>
                    )}
                </div>

                <div style={{
                    background: 'white', borderRadius: '14px',
                    padding: '24px', border: '1px solid var(--sp-gray-100)'
                }}>
                    <h3 style={{ margin: '0 0 20px', fontSize: '16px', fontWeight: '700' }}>
                        {t('uploadPage:comments')}
                        <span style={{
                            marginLeft: '8px', background: 'var(--sp-gray-50)', color: '#888',
                            padding: '2px 10px', borderRadius: '12px', fontSize: '13px', fontWeight: '400'
                        }}>
                            {video.comments?.length || 0}
                        </span>
                    </h3>

                    {video.comments?.length === 0 && (
                        <p style={{ color: '#aaa', fontSize: '14px', textAlign: 'center', padding: '20px 0' }}>
                            {t('uploadPage:noComments')}
                        </p>
                    )}

                    {video.comments?.map(c => (
                        <div key={c.id} style={{
                            display: 'flex', gap: '12px', marginBottom: '16px'
                        }}>
                            <div style={{
                                width: '38px', height: '38px', borderRadius: '50%',
                                background: '#1A1A1A', color: 'white',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '15px', fontWeight: 'bold', flexShrink: 0
                            }}>
                                {c.author?.charAt(0).toUpperCase()}
                            </div>
                            <div style={{
                                background: 'var(--sp-gray-50)', borderRadius: '10px',
                                padding: '10px 14px', flex: 1
                            }}>
                                <div style={{ fontSize: '13px', fontWeight: '700', marginBottom: '4px', color: '#333' }}>
                                    {c.author}
                                </div>
                                <div style={{ fontSize: '14px', color: '#444', lineHeight: '1.5' }}>{c.text}</div>
                            </div>
                        </div>
                    ))}

                    <form onSubmit={handleComment} style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
                        <input
                            value={comment}
                            onChange={e => setComment(e.target.value)}
                            placeholder={t('uploadPage:commentPlaceholder')}
                            required
                            style={{
                                flex: 1, padding: '11px 14px', border: '1.5px solid var(--sp-gray-200)',
                                borderRadius: '10px', fontSize: '14px', outline: 'none'
                            }}
                            onFocus={e => e.target.style.borderColor = '#EB3333'}
                            onBlur={e => e.target.style.borderColor = 'var(--sp-gray-200)'}
                        />
                        <button type="submit" style={{
                            padding: '11px 24px', background: '#1A1A1A', color: 'white',
                            border: 'none', borderRadius: '10px', fontSize: '14px',
                            fontWeight: '600', cursor: 'pointer'
                        }}>
                            {t('uploadPage:sendComment')}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}
